import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import {
  FlaskConical, Plus, Send, Trash2, X, BarChart3,
  Users, Smartphone, ChevronDown, ChevronUp, Trophy
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PushABTests = () => {
  const [tests, setTests] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showResults, setShowResults] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [sending, setSending] = useState(null);
  const [expanded, setExpanded] = useState(true);

  const [form, setForm] = useState({
    name: '',
    variant_a_title: '',
    variant_a_message: '',
    variant_b_title: '',
    variant_b_message: '',
    target_platform: 'all',
    split_ratio: 50,
  });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchTests = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/push/ab-tests`, { headers });
      setTests(res.data || []);
    } catch (err) {
      console.error('AB test fetch error:', err);
    }
  }, []);

  useEffect(() => { fetchTests(); }, [fetchTests]);

  const handleCreate = async () => {
    if (!form.name.trim() || !form.variant_a_title.trim() || !form.variant_a_message.trim() || !form.variant_b_title.trim() || !form.variant_b_message.trim()) {
      toast.error('Bitte alle Felder ausfüllen');
      return;
    }
    try {
      await axios.post(`${API}/admin/push/ab-tests`, form, { headers });
      toast.success('A/B-Test erstellt');
      setForm({ name: '', variant_a_title: '', variant_a_message: '', variant_b_title: '', variant_b_message: '', target_platform: 'all', split_ratio: 50 });
      setShowCreate(false);
      fetchTests();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Erstellen');
    }
  };

  const handleSend = async (testId) => {
    setSending(testId);
    try {
      const res = await axios.post(`${API}/admin/push/ab-tests/${testId}/send`, {}, { headers });
      toast.success(res.data.message);
      fetchTests();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Senden');
    } finally {
      setSending(null);
    }
  };

  const handleDelete = async (testId) => {
    try {
      await axios.delete(`${API}/admin/push/ab-tests/${testId}`, { headers });
      toast.success('A/B-Test gelöscht');
      fetchTests();
      if (showResults === testId) setShowResults(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const handleViewResults = async (testId) => {
    if (showResults === testId) {
      setShowResults(null);
      setDetailData(null);
      return;
    }
    try {
      const res = await axios.get(`${API}/admin/push/ab-tests/${testId}`, { headers });
      setDetailData(res.data);
      setShowResults(testId);
    } catch (err) {
      toast.error('Fehler beim Laden der Ergebnisse');
    }
  };

  const statusBadge = (status) => {
    const map = {
      draft: { label: 'Entwurf', cls: 'bg-gray-100 text-gray-600' },
      sent: { label: 'Gesendet', cls: 'bg-blue-100 text-blue-700' },
      delivered: { label: 'Zugestellt', cls: 'bg-green-100 text-green-700' },
      partial: { label: 'Teilweise', cls: 'bg-amber-100 text-amber-700' },
      queued: { label: 'Wartend', cls: 'bg-gray-100 text-gray-600' },
    };
    const s = map[status] || { label: status, cls: 'bg-gray-100 text-gray-600' };
    return <Badge className={`text-[10px] ${s.cls}`}>{s.label}</Badge>;
  };

  return (
    <Card data-testid="ab-test-card">
      <CardHeader>
        <button
          className="flex items-center justify-between w-full"
          onClick={() => setExpanded(!expanded)}
          data-testid="ab-test-toggle"
        >
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <FlaskConical className="w-5 h-5 text-indigo-600" />
              A/B-Tests für Push-Benachrichtigungen
            </CardTitle>
            <CardDescription className="text-left mt-1">
              Teste verschiedene Nachrichten-Varianten und vergleiche die Performance.
            </CardDescription>
          </div>
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </CardHeader>

      {expanded && (
        <CardContent className="space-y-4">
          {/* Create button */}
          {!showCreate && (
            <Button
              variant="outline"
              onClick={() => setShowCreate(true)}
              data-testid="ab-test-create-btn"
              className="w-full border-dashed border-indigo-300 text-indigo-600 hover:bg-indigo-50"
            >
              <Plus className="w-4 h-4 mr-1.5" />
              Neuen A/B-Test erstellen
            </Button>
          )}

          {/* Create form */}
          {showCreate && (
            <div className="border border-indigo-200 rounded-lg p-4 bg-indigo-50/30 space-y-4" data-testid="ab-test-create-form">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-indigo-800">Neuer A/B-Test</h3>
                <button onClick={() => setShowCreate(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="space-y-2">
                <Label>Testname *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="z.B. Rabatt-Titel-Test"
                  data-testid="ab-test-name-input"
                />
              </div>

              {/* Variant A */}
              <div className="border rounded-lg p-3 bg-white space-y-2">
                <div className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-blue-500 text-white text-xs font-bold flex items-center justify-center">A</span>
                  <span className="text-sm font-medium">Variante A</span>
                </div>
                <Input
                  value={form.variant_a_title}
                  onChange={(e) => setForm({ ...form, variant_a_title: e.target.value })}
                  placeholder="Titel A"
                  data-testid="ab-test-a-title"
                />
                <textarea
                  value={form.variant_a_message}
                  onChange={(e) => setForm({ ...form, variant_a_message: e.target.value })}
                  className="w-full min-h-[60px] border rounded-md p-2 text-sm resize-none"
                  placeholder="Nachricht A"
                  data-testid="ab-test-a-message"
                />
              </div>

              {/* Variant B */}
              <div className="border rounded-lg p-3 bg-white space-y-2">
                <div className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-orange-500 text-white text-xs font-bold flex items-center justify-center">B</span>
                  <span className="text-sm font-medium">Variante B</span>
                </div>
                <Input
                  value={form.variant_b_title}
                  onChange={(e) => setForm({ ...form, variant_b_title: e.target.value })}
                  placeholder="Titel B"
                  data-testid="ab-test-b-title"
                />
                <textarea
                  value={form.variant_b_message}
                  onChange={(e) => setForm({ ...form, variant_b_message: e.target.value })}
                  className="w-full min-h-[60px] border rounded-md p-2 text-sm resize-none"
                  placeholder="Nachricht B"
                  data-testid="ab-test-b-message"
                />
              </div>

              {/* Split ratio */}
              <div className="space-y-2">
                <Label>Aufteilung (A / B)</Label>
                <div className="flex items-center gap-3">
                  <input
                    type="range"
                    min="10"
                    max="90"
                    step="5"
                    value={form.split_ratio}
                    onChange={(e) => setForm({ ...form, split_ratio: parseInt(e.target.value) })}
                    className="flex-1 accent-indigo-600"
                    data-testid="ab-test-split-slider"
                  />
                  <span className="text-sm font-medium text-gray-700 whitespace-nowrap w-20 text-center">
                    {form.split_ratio}% / {100 - form.split_ratio}%
                  </span>
                </div>
                <div className="flex justify-between text-[10px] text-gray-400">
                  <span>Mehr A</span>
                  <span>Mehr B</span>
                </div>
              </div>

              {/* Target platform */}
              <div className="space-y-2">
                <Label>Zielgruppe</Label>
                <div className="flex gap-2">
                  {[
                    { value: 'all', label: 'Alle', icon: Users },
                    { value: 'ios', label: 'iOS', icon: Smartphone },
                    { value: 'android', label: 'Android', icon: Smartphone },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setForm({ ...form, target_platform: opt.value })}
                      data-testid={`ab-test-target-${opt.value}`}
                      className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border transition-colors ${
                        form.target_platform === opt.value
                          ? 'border-indigo-500 bg-indigo-50 text-indigo-700 font-medium'
                          : 'border-gray-200 text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      <opt.icon className="w-3.5 h-3.5" />
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => setShowCreate(false)}>Abbrechen</Button>
                <Button
                  onClick={handleCreate}
                  data-testid="ab-test-create-submit"
                  className="bg-indigo-600 hover:bg-indigo-700"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Test erstellen
                </Button>
              </div>
            </div>
          )}

          {/* Tests list */}
          {tests.length === 0 && !showCreate && (
            <p className="text-sm text-gray-400 text-center py-4">
              Noch keine A/B-Tests erstellt.
            </p>
          )}

          {tests.map((test) => (
            <div
              key={test.id}
              className="border rounded-lg p-4 space-y-3 bg-white"
              data-testid={`ab-test-item-${test.id}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 min-w-0">
                  <FlaskConical className="w-4 h-4 text-indigo-500 shrink-0" />
                  <span className="text-sm font-medium truncate">{test.name}</span>
                  {statusBadge(test.status)}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {test.status === 'draft' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleSend(test.id)}
                      disabled={sending === test.id}
                      data-testid={`ab-test-send-${test.id}`}
                      className="text-xs h-7 border-green-300 text-green-700 hover:bg-green-50"
                    >
                      <Send className="w-3 h-3 mr-1" />
                      {sending === test.id ? '...' : 'Senden'}
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleViewResults(test.id)}
                    data-testid={`ab-test-results-${test.id}`}
                    className="text-xs h-7"
                  >
                    <BarChart3 className="w-3 h-3 mr-1" />
                    Ergebnisse
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDelete(test.id)}
                    data-testid={`ab-test-delete-${test.id}`}
                    className="text-xs h-7 text-red-500 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>

              {/* Compact variant preview */}
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="border rounded p-2 bg-blue-50/50">
                  <div className="flex items-center gap-1 mb-1">
                    <span className="w-4 h-4 rounded-full bg-blue-500 text-white text-[9px] font-bold flex items-center justify-center">A</span>
                    <span className="font-medium text-blue-800">{test.split_ratio || 50}%</span>
                  </div>
                  <p className="font-medium text-gray-800 truncate">{test.variant_a?.title}</p>
                  <p className="text-gray-500 truncate">{test.variant_a?.message}</p>
                </div>
                <div className="border rounded p-2 bg-orange-50/50">
                  <div className="flex items-center gap-1 mb-1">
                    <span className="w-4 h-4 rounded-full bg-orange-500 text-white text-[9px] font-bold flex items-center justify-center">B</span>
                    <span className="font-medium text-orange-800">{100 - (test.split_ratio || 50)}%</span>
                  </div>
                  <p className="font-medium text-gray-800 truncate">{test.variant_b?.title}</p>
                  <p className="text-gray-500 truncate">{test.variant_b?.message}</p>
                </div>
              </div>

              {/* Timestamps */}
              <div className="text-[10px] text-gray-400 flex gap-3">
                <span>Erstellt: {test.created_at ? new Date(test.created_at).toLocaleString('de-CH') : '-'}</span>
                {test.sent_at && <span>Gesendet: {new Date(test.sent_at).toLocaleString('de-CH')}</span>}
              </div>

              {/* Results panel */}
              {showResults === test.id && detailData && (
                <div className="border-t pt-3 space-y-3" data-testid={`ab-test-results-panel-${test.id}`}>
                  <h4 className="text-sm font-semibold flex items-center gap-1.5">
                    <BarChart3 className="w-4 h-4 text-indigo-500" />
                    Testergebnisse
                  </h4>

                  <div className="grid grid-cols-2 gap-3">
                    {/* Variant A Stats */}
                    <div className={`border rounded-lg p-3 ${detailData.stats?.winner === 'A' ? 'border-blue-400 bg-blue-50' : 'bg-gray-50'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-1.5">
                          <span className="w-5 h-5 rounded-full bg-blue-500 text-white text-[10px] font-bold flex items-center justify-center">A</span>
                          <span className="text-xs font-semibold">Variante A</span>
                        </div>
                        {detailData.stats?.winner === 'A' && (
                          <Trophy className="w-4 h-4 text-blue-600" />
                        )}
                      </div>
                      <div className="space-y-1 text-xs">
                        <div className="flex justify-between">
                          <span className="text-gray-500">Gesendet</span>
                          <span className="font-medium">{detailData.stats?.a_sent || 0}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Geöffnet</span>
                          <span className="font-medium">{detailData.stats?.a_opened || 0}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Öffnungsrate</span>
                          <span className="font-bold text-blue-700">{detailData.stats?.a_open_rate || 0}%</span>
                        </div>
                      </div>
                      {/* Bar */}
                      <div className="mt-2 h-2 rounded-full bg-gray-200 overflow-hidden">
                        <div
                          className="h-full bg-blue-500 rounded-full transition-all"
                          style={{ width: `${detailData.stats?.a_open_rate || 0}%` }}
                        />
                      </div>
                    </div>

                    {/* Variant B Stats */}
                    <div className={`border rounded-lg p-3 ${detailData.stats?.winner === 'B' ? 'border-orange-400 bg-orange-50' : 'bg-gray-50'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-1.5">
                          <span className="w-5 h-5 rounded-full bg-orange-500 text-white text-[10px] font-bold flex items-center justify-center">B</span>
                          <span className="text-xs font-semibold">Variante B</span>
                        </div>
                        {detailData.stats?.winner === 'B' && (
                          <Trophy className="w-4 h-4 text-orange-600" />
                        )}
                      </div>
                      <div className="space-y-1 text-xs">
                        <div className="flex justify-between">
                          <span className="text-gray-500">Gesendet</span>
                          <span className="font-medium">{detailData.stats?.b_sent || 0}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Geöffnet</span>
                          <span className="font-medium">{detailData.stats?.b_opened || 0}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Öffnungsrate</span>
                          <span className="font-bold text-orange-700">{detailData.stats?.b_open_rate || 0}%</span>
                        </div>
                      </div>
                      {/* Bar */}
                      <div className="mt-2 h-2 rounded-full bg-gray-200 overflow-hidden">
                        <div
                          className="h-full bg-orange-500 rounded-full transition-all"
                          style={{ width: `${detailData.stats?.b_open_rate || 0}%` }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Winner summary */}
                  {detailData.stats?.winner && detailData.stats.winner !== 'tie' && (detailData.stats.a_sent > 0 || detailData.stats.b_sent > 0) && (
                    <div className="flex items-center gap-2 p-2 rounded-lg bg-indigo-50 border border-indigo-200 text-xs">
                      <Trophy className="w-4 h-4 text-indigo-600 shrink-0" />
                      <span className="text-indigo-800">
                        <strong>Variante {detailData.stats.winner}</strong> gewinnt mit{' '}
                        {detailData.stats.winner === 'A' ? detailData.stats.a_open_rate : detailData.stats.b_open_rate}% Öffnungsrate
                      </span>
                    </div>
                  )}

                  {detailData.stats?.a_sent === 0 && detailData.stats?.b_sent === 0 && (
                    <p className="text-xs text-gray-400 text-center">
                      Noch keine Daten verfügbar. Sende den Test zuerst.
                    </p>
                  )}

                  {/* FCM delivery info */}
                  {(detailData.stats?.a_fcm_success > 0 || detailData.stats?.b_fcm_success > 0) && (
                    <div className="text-[10px] text-gray-400 text-center">
                      FCM-Zustellung: A ({detailData.stats.a_fcm_success} ok, {detailData.stats.a_fcm_failed} fehlg.) | B ({detailData.stats.b_fcm_success} ok, {detailData.stats.b_fcm_failed} fehlg.)
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </CardContent>
      )}
    </Card>
  );
};
